import { useEffect, useState, useCallback } from "react";
import { RefreshCw, Lock, ArrowUp, ArrowDown, ShieldCheck, XOctagon } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { PnL, fmt, PageHeader, Table, Td } from "../../components/dashboard/Shared";
import { LiveBadge } from "./mgrShared";

const Stat = ({ label, value, accent }) => (
  <div className="bg-white/[0.03] rounded-lg px-3 py-2 text-center">
    <p className="text-[10px] uppercase text-gray-500">{label}</p>
    <p className={`tabular mt-0.5 ${accent || "text-white"}`}>{value}</p>
  </div>
);

export default function ManagerLivePositions() {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const [trail, setTrail] = useState(null);   // single position being trailed
  const [lock, setLock] = useState(null);
  const [newSl, setNewSl] = useState("");
  const [saving, setSaving] = useState(false);
  const [modal, setModal] = useState(null);    // 'global-trail' | 'close-all'
  const [gMode, setGMode] = useState("breakeven");
  const [gDist, setGDist] = useState("");
  const [opBusy, setOpBusy] = useState(false);
  const [result, setResult] = useState(null);  // {title, summary, results}

  const load = useCallback(async () => {
    const { data } = await api.get("/manager/live-positions");
    setRows(data.positions);
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load]);

  const refresh = async () => { setBusy(true); await load(); setBusy(false); };

  const openTrail = async (p) => {
    setTrail(p); setNewSl(p.sl ?? ""); setLock(null);
    try {
      const { data } = await api.get(`/manager/mt5-clients/${p.account_id}/positions/${p.id}/sl-lock`);
      setLock(data.lock || {});
    } catch { setLock({}); }
  };
  const isBuy = trail?.type === "buy";
  const lockedSl = lock?.locked_sl ?? trail?.sl;

  const submitTrail = async () => {
    const val = Number(newSl);
    if (!val || val <= 0) { toast.error("Enter a valid SL price"); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/manager/mt5-clients/${trail.account_id}/positions/${trail.id}/trail-sl`, { new_sl: val });
      toast.success(`SL locked at ${data.locked_sl} (${data.side.toUpperCase()})`);
      setTrail(null); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "SL change rejected"); }
    setSaving(false);
  };

  // aggregate counts for the global modals
  const agg = (() => {
    const r = rows || [];
    return {
      positions: r.length,
      buy: r.filter((p) => p.type === "buy").length,
      sell: r.filter((p) => p.type === "sell").length,
      accounts: new Set(r.map((p) => p.account_id)).size,
      clients: new Set(r.map((p) => p.client_name)).size,
      volume: r.reduce((a, p) => a + (p.volume || 0), 0),
      floating: r.reduce((a, p) => a + (p.profit || 0), 0),
    };
  })();

  const runGlobalTrail = async () => {
    if (gMode === "distance" && (!Number(gDist) || Number(gDist) <= 0)) { toast.error("Enter a valid trailing distance"); return; }
    setOpBusy(true);
    try {
      const body = gMode === "distance" ? { mode: "distance", distance: Number(gDist) } : { mode: "breakeven" };
      const { data } = await api.post("/manager/global-trail-sl", body);
      setModal(null);
      setResult({ title: "Global Trailing SL Result", summary: { Total: data.total, Successful: data.success, Skipped: data.skipped, Rejected: data.rejected, Failed: data.failed }, results: data.results });
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Global trailing failed"); }
    setOpBusy(false);
  };

  const runCloseAll = async () => {
    setOpBusy(true);
    try {
      const { data } = await api.post("/manager/close-all-positions");
      const res = (data.results || []).map((r) => ({ login: r.login, broker: r.broker, symbol: "ALL", status: r.ok ? "success" : "failed", reason: r.error || "", final: `${r.closed ?? 0}/${r.total ?? 0}` }));
      setModal(null);
      setResult({ title: "Close All Trades Result", summary: { Accounts: data.accounts, "Positions Closed": data.closed, Failed: res.filter((r) => r.status === "failed").length }, results: res });
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Close all failed"); }
    setOpBusy(false);
  };

  return (
    <div>
      <PageHeader title="Live Positions" sub="All open positions across active MT5 accounts — one-way locked trailing SL">
        <button data-testid="global-trail-sl-btn" onClick={() => setModal("global-trail")} disabled={!agg.positions}
          className="px-3 py-1.5 text-xs rounded-lg font-medium flex items-center gap-2 bg-[#7000FF] text-white hover:bg-[#7d1aff] transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
          <ShieldCheck size={13} /> Global Trailing SL
        </button>
        <button data-testid="global-close-all-btn" onClick={() => setModal("close-all")} disabled={!agg.positions}
          className="px-3 py-1.5 text-xs rounded-lg font-medium flex items-center gap-2 bg-[#FF3B30] text-white hover:bg-[#ff5147] transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
          <XOctagon size={13} /> Close All Trades{agg.positions ? ` (${agg.positions})` : ""}
        </button>
        <button data-testid="positions-refresh-btn" onClick={refresh} disabled={busy} className="btn-outline px-3 py-1.5 text-xs flex items-center gap-2 disabled:opacity-50">
          <RefreshCw size={12} className={busy ? "animate-spin" : ""} /> Refresh
        </button>
      </PageHeader>

      <div className="card-dark p-4 sm:p-5">
        {!rows ? (
          <div className="skeleton h-32" />
        ) : rows.length === 0 ? (
          <p className="text-sm text-gray-500 py-8 text-center" data-testid="positions-empty">No open positions across your active accounts right now.</p>
        ) : (
          <div className="overflow-x-auto -mx-2 px-2">
            <Table headers={["Client", "Broker", "MT5", "Symbol", "Side", "Volume", "Entry", "Current", "SL", "TP", "Floating P&L", ""]} testId="mgr-positions-table">
              {rows.map((p) => (
                <tr key={`${p.account_id}-${p.id}`} data-testid={`mgr-position-row-${p.id}`} className="border-b border-white/5 hover:bg-white/[0.03]">
                  <Td className="text-white">{p.client_name}</Td>
                  <Td className="text-xs">{p.broker}</Td>
                  <Td className="tabular text-xs">{p.login}</Td>
                  <Td className="tabular text-white">{p.symbol}</Td>
                  <Td><span className={p.type === "buy" ? "text-[#00E676]" : "text-[#FF3B30]"}>{p.type?.toUpperCase()}</span></Td>
                  <Td className="tabular">{fmt(p.volume)}</Td>
                  <Td className="tabular text-xs">{p.open_price}</Td>
                  <Td className="tabular text-xs text-[#00F0FF]">{p.current_price ?? "—"}</Td>
                  <Td className="tabular text-xs text-[#FF3B30]">{p.sl ?? "—"}</Td>
                  <Td className="tabular text-xs text-[#00E676]">{p.tp ?? "—"}</Td>
                  <Td><PnL value={p.profit} /></Td>
                  <Td>
                    <button data-testid={`trail-sl-btn-${p.id}`} onClick={() => openTrail(p)}
                      className="text-[11px] px-2.5 py-1 rounded-full border border-[#7000FF]/50 text-[#b388ff] hover:bg-[#7000FF]/10 inline-flex items-center gap-1 transition-colors">
                      <Lock size={11} /> Trail SL
                    </button>
                  </Td>
                </tr>
              ))}
            </Table>
            <div className="flex items-center gap-2 mt-3"><LiveBadge state="live" /><span className="text-[10px] text-gray-600">Positions from MetaApi live snapshot (5s).</span></div>
          </div>
        )}
      </div>

      {/* ── single-position trailing SL ── */}
      {trail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="trail-sl-modal" onClick={() => setTrail(null)}>
          <div className="card-dark w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-1"><Lock size={15} className="text-[#b388ff]" /><p className="text-lg text-white">Trailing Stop-Loss</p></div>
            <p className="text-xs text-gray-500 mb-4">{trail.client_name} · {trail.symbol} · <span className={isBuy ? "text-[#00E676]" : "text-[#FF3B30]"}>{trail.type?.toUpperCase()}</span> · {fmt(trail.volume)} lots</p>
            <div className={`text-xs rounded-lg px-3 py-2 mb-4 flex items-center gap-2 ${isBuy ? "bg-[#00E676]/10 text-[#00E676]" : "bg-[#FF3B30]/10 text-[#FF3B30]"}`}>
              {isBuy ? <ArrowUp size={13} /> : <ArrowDown size={13} />}
              {isBuy ? "SL can only move UP. It cannot be moved back down." : "SL can only move DOWN. It cannot be moved back up."}
            </div>
            <div className="grid grid-cols-3 gap-2 mb-4 text-center text-xs">
              <div className="bg-white/[0.03] rounded-lg p-2"><p className="text-[10px] uppercase text-gray-500">Entry</p><p className="tabular text-white mt-1">{trail.open_price}</p></div>
              <div className="bg-white/[0.03] rounded-lg p-2"><p className="text-[10px] uppercase text-gray-500">Current</p><p className="tabular text-[#00F0FF] mt-1">{trail.current_price ?? "—"}</p></div>
              <div className="bg-white/[0.03] rounded-lg p-2"><p className="text-[10px] uppercase text-gray-500">Locked SL</p><p className="tabular text-[#FFD600] mt-1" data-testid="trail-locked-sl">{lockedSl ?? "—"}</p></div>
            </div>
            <label className="label-xs">New Stop Loss</label>
            <input data-testid="trail-sl-input" type="number" step="0.00001" value={newSl} onChange={(e) => setNewSl(e.target.value)} className="input-dark !py-2 text-sm mt-1 mb-2" placeholder="e.g. 4400.00" />
            <p className="text-[11px] text-gray-500 mb-4">Current SL: <span className="text-white">{trail.sl ?? "none"}</span> · TP: <span className="text-white">{trail.tp ?? "none"}</span>{lock?.mods ? <span className="text-gray-600"> · {lock.mods} prior move(s)</span> : null}</p>
            <div className="flex gap-3">
              <button onClick={() => setTrail(null)} className="btn-outline flex-1 py-2.5 text-sm">Cancel</button>
              <button data-testid="trail-sl-confirm" onClick={submitTrail} disabled={saving} className="flex-1 py-2.5 text-sm rounded-lg font-medium bg-[#7000FF] text-white hover:bg-[#7d1aff] transition-colors disabled:opacity-50">{saving ? "Applying…" : "Apply & Lock"}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── GLOBAL trailing SL config ── */}
      {modal === "global-trail" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="global-trail-modal" onClick={() => setModal(null)}>
          <div className="card-dark w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-1"><ShieldCheck size={16} className="text-[#b388ff]" /><p className="text-lg text-white">Global Trailing SL</p></div>
            <p className="text-xs text-gray-500 mb-4">Applies the one-way locked rule to every eligible open position. Positions that would be weakened are skipped automatically.</p>
            <div className="grid grid-cols-4 gap-2 mb-4">
              <Stat label="Clients" value={agg.clients} /><Stat label="Accounts" value={agg.accounts} />
              <Stat label="Positions" value={agg.positions} /><Stat label="Buy / Sell" value={`${agg.buy}/${agg.sell}`} />
              <Stat label="Volume" value={fmt(agg.volume)} /><Stat label="Floating" value={<PnL value={Math.round(agg.floating * 100) / 100} />} accent="" />
            </div>
            <label className="label-xs">Mode</label>
            <div className="flex gap-2 mt-1 mb-3">
              <button data-testid="gmode-breakeven" onClick={() => setGMode("breakeven")} className={`flex-1 py-2 text-xs rounded-lg border transition-colors ${gMode === "breakeven" ? "border-[#7000FF] bg-[#7000FF]/10 text-white" : "border-white/10 text-gray-400"}`}>Move to Break-even (SL → entry)</button>
              <button data-testid="gmode-distance" onClick={() => setGMode("distance")} className={`flex-1 py-2 text-xs rounded-lg border transition-colors ${gMode === "distance" ? "border-[#7000FF] bg-[#7000FF]/10 text-white" : "border-white/10 text-gray-400"}`}>Trail by distance</button>
            </div>
            {gMode === "distance" && (
              <div className="mb-3">
                <label className="label-xs">Trailing distance (price units from current)</label>
                <input data-testid="gdist-input" type="number" step="0.00001" value={gDist} onChange={(e) => setGDist(e.target.value)} className="input-dark !py-2 text-sm mt-1" placeholder="e.g. 5" />
                <p className="text-[11px] text-gray-500 mt-1">BUY SL = current − distance · SELL SL = current + distance. Each still obeys its locked level.</p>
              </div>
            )}
            <div className="flex gap-3 mt-4">
              <button onClick={() => setModal(null)} className="btn-outline flex-1 py-2.5 text-sm">Cancel</button>
              <button data-testid="global-trail-confirm" onClick={runGlobalTrail} disabled={opBusy} className="flex-1 py-2.5 text-sm rounded-lg font-medium bg-[#7000FF] text-white hover:bg-[#7d1aff] transition-colors disabled:opacity-50">{opBusy ? "Processing…" : `Apply to ${agg.positions} positions`}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── GLOBAL close all ── */}
      {modal === "close-all" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="close-all-modal" onClick={() => setModal(null)}>
          <div className="card-dark w-full max-w-lg p-6 border border-[#FF3B30]/30" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-1"><XOctagon size={16} className="text-[#FF3B30]" /><p className="text-lg text-white">Close All Trades</p></div>
            <p className="text-xs text-[#FF3B30] mb-4">You are about to close ALL currently open ChartzFx-managed positions at market price. This affects real money and cannot be undone.</p>
            <div className="grid grid-cols-4 gap-2 mb-4">
              <Stat label="Clients" value={agg.clients} /><Stat label="Accounts" value={agg.accounts} />
              <Stat label="Positions" value={agg.positions} /><Stat label="Buy / Sell" value={`${agg.buy}/${agg.sell}`} />
              <Stat label="Volume" value={fmt(agg.volume)} /><Stat label="Floating" value={<PnL value={Math.round(agg.floating * 100) / 100} />} accent="" />
            </div>
            <div className="flex gap-3 mt-2">
              <button data-testid="close-all-cancel" onClick={() => setModal(null)} className="btn-outline flex-1 py-2.5 text-sm">Cancel</button>
              <button data-testid="close-all-confirm" onClick={runCloseAll} disabled={opBusy} className="flex-1 py-2.5 text-sm rounded-lg font-medium bg-[#FF3B30] text-white hover:bg-[#ff5147] transition-colors disabled:opacity-50">{opBusy ? "Closing…" : "Close All Trades"}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── result modal ── */}
      {result && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="op-result-modal" onClick={() => setResult(null)}>
          <div className="card-dark w-full max-w-3xl p-6 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <p className="text-lg text-white mb-3">{result.title}</p>
            <div className="flex flex-wrap gap-2 mb-4">
              {Object.entries(result.summary).map(([k, v]) => (
                <div key={k} className="bg-white/[0.03] rounded-lg px-3 py-2 text-center min-w-[90px]">
                  <p className="text-[10px] uppercase text-gray-500">{k}</p><p className="tabular text-white mt-0.5">{v}</p>
                </div>
              ))}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="op-result-table">
                <thead><tr className="text-gray-500 text-left">
                  <th className="py-1.5 font-normal">Client</th><th className="py-1.5 font-normal">Broker</th><th className="py-1.5 font-normal">MT5</th>
                  <th className="py-1.5 font-normal">Symbol</th><th className="py-1.5 font-normal">Side</th><th className="py-1.5 font-normal text-right">Prev→Final</th>
                  <th className="py-1.5 font-normal">Result</th><th className="py-1.5 font-normal">Reason</th>
                </tr></thead>
                <tbody>
                  {result.results.map((r, i) => (
                    <tr key={i} className="border-t border-white/5">
                      <td className="py-1.5 text-white">{r.client || "—"}</td>
                      <td className="py-1.5">{r.broker || "—"}</td>
                      <td className="py-1.5 tabular">{r.login || "—"}</td>
                      <td className="py-1.5 tabular">{r.symbol || "—"}</td>
                      <td className="py-1.5 uppercase">{r.side || "—"}</td>
                      <td className="py-1.5 tabular text-right">{r.previous_locked != null ? `${r.previous_locked}→${r.final}` : (r.final ?? "—")}</td>
                      <td className={`py-1.5 uppercase ${r.status === "success" ? "text-[#00E676]" : r.status === "skipped" ? "text-[#FFD600]" : "text-[#FF3B30]"}`}>{r.status}</td>
                      <td className="py-1.5 text-gray-500">{r.reason || ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end mt-4"><button data-testid="op-result-close" onClick={() => setResult(null)} className="btn-outline px-4 py-2 text-sm">Done</button></div>
          </div>
        </div>
      )}
    </div>
  );
}
